import { ref, onUnmounted, onMounted }  from "vue";
import Store                            from "./store.js";

export * from "./utils/diff.js";

function mergeAbortSignals(signals: AbortSignal[]): AbortSignal {
    if (typeof AbortSignal.any === "function")
        return AbortSignal.any(signals);

    const abortController = new AbortController();

    for (const signal of signals) {
        if (signal.aborted) {
            abortController.abort();
            break;
        }

        signal.addEventListener("abort", () => abortController.abort(), { once: true });
    }

    return abortController.signal;
}

export function useFetch(): { fetch: (url: string | URL, options?: RequestInit) => Promise<Response> } {
    const abortController = new AbortController();

    onUnmounted(() => {
        abortController.abort();
    });

    return {
        async fetch(url: string | URL, options: RequestInit = {}): Promise<Response> {
            const signal = options.signal
                ? mergeAbortSignals([ abortController.signal, options.signal ])
                : abortController.signal;
            const res = await fetch(url, Object.assign(options, { signal }));

            if (res.status === 401) {
                location.assign("https://logscord.com/api/oauth2");
                throw res;
            }

            return res;
        },
    };
}

export function useLogin() {
    const refLoading    = ref<boolean>(true);
    const refLogged     = ref<boolean>(false);

    onMounted(async () => {
        try {
            const res = await fetch("/api/@me");

            if (res.status === 200) {
                Store.me = await res.json();
                const guildMember = Store.me!.guildMember;

                Store.selectedGuild = guildMember
                    ? Store.me!.guilds.find((guild) => guild.id === guildMember.guildId) || null
                    : null;
                refLogged.value     = true;
                return;
            }

            if (res.status === 401) {
                refLogged.value = false;
                // @TODO: Remove
                // if (route.path !== "/report")
                //     location.assign("/api/oauth2");
                return;
            }

            if (res.status === 403) {
                refLogged.value = false;
                return;
            }

            console.log(res.status, res.statusText);
            refLogged.value = false;

        } finally {
            refLoading.value = false;
        }
    });

    return [
        refLoading,
        refLogged,
    ] as const;
}

export interface Member {
    id: string;
    user: {
        username: string;
        globalName: string;
        avatar: string;
        banner: string | null;
        bot?: boolean;
        badges?: string[];
    };
    serverNickname: string;
    serverAvatar: string;
};

export function getMemberDisplayName(member: Member): string {
    return member.serverNickname
        || member.user.globalName
        || member.user.username;
}

export function getMemberAvatar(member: Member): string {
    return member.serverAvatar
        || member.user.avatar
        || `https://cdn.discordapp.com/embed/avatars/${(BigInt(member.id) >> 22n) % 6n}.png`;
}

export function getMemberBanner(member: Member): string | null {
    return member.user.banner;
}

export function getMemberTag(member: Member): string {
    return member.user.username;
}

export function getFormattedTime(ms: number): string {
    const seconds   = ms / 1000;
    const minutes   = seconds / 60;
    const hours     = minutes / 60;

    if (hours >= 1)
        return `${Math.floor(hours)}h${Math.floor(minutes % 60).toString().padStart(2, "0")}m`;

    if (minutes >= 1)
        return `${Math.floor(minutes % 60).toString().padStart(2, "0")}m${Math.floor(seconds % 60).toString().padStart(2, "0")}s`;

    return `${Math.floor(seconds % 60).toString().padStart(2, "0")}s`;
}

export function getFormattedDate(timestamp: number): string {
    const date = new Date(timestamp);

    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")} à ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
}
